import React, { useState, useEffect } from "react";
import ReactSlick from "react-slick";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { Link } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import slider1 from "../assets/slider1.mp4";
import slider2 from "../assets/slider2.mp4";
import slider3 from "../assets/slider3.mp4";

const slides = [
  {
    image: slider1,
    alt: "BFI team collaborating on software planning",
    title: "Welcome to BFI",
    description:
      "BFI designs personalized, performance-driven software solutions to streamline your workflows, secure your data, and maximize profitability.",
    imageClass: "object-[62%_center] sm:object-[58%_center] lg:object-center",
    badge: "Trusted by 200+ Clients",
  },
  {
    image: slider2,
    alt: "Digital compliance and technology interface",
    title: "Stay Up To Date With Your Business",
    description:
      "When it comes to app development, we believe that full customization is the key, not the exception.",
    imageClass: "object-[52%_center] sm:object-center lg:object-center",
    badge: "8+ Years of Excellence",
  },
  {
    image: slider3,
    alt: "Strategic business growth and digital transformation",
    title: "Grow Beyond Limits",
    description:
      "Leverage our expertise to scale your business efficiently and achieve your long-term goals.",
    imageClass: "object-[56%_center] sm:object-[60%_center] lg:object-center",
    badge: "5+ Products",
  },
];

const arrowBaseClass =
  "absolute top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-slate-950/40 text-white/90 shadow-lg shadow-black/25 backdrop-blur-sm transition hover:bg-slate-950/70 hover:text-white md:h-12 md:w-12";

const Slider = ReactSlick?.default ?? ReactSlick;

const ArrowButton = ({ onClick, direction }) => {
  const isPrev = direction === "prev";
  const Icon = isPrev ? IoChevronBack : IoChevronForward;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isPrev ? "Previous slide" : "Next slide"}
      className={`${arrowBaseClass} ${isPrev ? "left-4 md:left-6" : "right-4 md:right-6"}`}
    >
      <Icon className="text-lg md:text-xl" />
    </button>
  );
};

{/* mobile hero */}
const MobileHero = ({ openDemo }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const slide = slides[current];

  return (
    <section
      id="hero"
      data-theme="dark"
      className="relative bg-gradient-to-b from-[#060b13] via-[#0b172a] to-[#050a12] overflow-hidden pt-[82px] md:hidden h-[450px] flex flex-col justify-center rounded-b-[2.5rem] border-b border-blue-900/30"
    >
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />
      {/* gradient blobs */}
      <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-blue-600/30 blur-[80px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-indigo-600/20 blur-[80px] pointer-events-none" />

      <div className="relative z-10 px-5 pt-8 pb-8">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-600/20 px-3 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">
            {slide.badge}
          </span>
        </div>

        <div className="min-h-[150px] sm:min-h-[160px]">
          <h1
            key={current + "t"}
            className="font-teko text-3xl sm:text-4xl font-bold uppercase text-white leading-tight tracking-wide mb-3 animate-fade-in"
          >
            {slide.title}
          </h1>

          <p
            key={current + "d"}
            className="text-xs sm:text-sm leading-5 sm:leading-6 text-white/90 mb-6"
          >
            {slide.description}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={openDemo}
            className="inline-flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-900/40"
          >
            Request a Demo
          </button>
          <Link
            to="/product"
            className="inline-flex items-center px-5 py-2.5 border border-white/20 bg-white/5 text-white/80 rounded-lg font-bold text-xs uppercase tracking-widest transition-all hover:bg-white/10"
          >
            Our Products
          </Link>
        </div>

        <div className="flex items-center gap-2 mt-6">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrent(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`rounded-full transition-all duration-300 ${
                i === current
                  ? "w-6 h-1.5 bg-blue-400"
                  : "w-1.5 h-1.5 bg-white/25"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

{/* desktop slider */}
const DesktopSlider = () => {
  const sliderSettings = {
    infinite: true,
    autoplay: true,
    autoplaySpeed: 5000,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    pauseOnHover: false,
    nextArrow: <ArrowButton direction="next" />,
    prevArrow: <ArrowButton direction="prev" />,
  };

  return (
    <section
      id="hero"
      className="relative hidden md:block overflow-hidden bg-slate-950 [&_.slick-dots_li.slick-active_button]:bg-white [&_.slick-list]:h-full [&_.slick-slide>div]:h-full [&_.slick-slider]:h-full [&_.slick-track]:flex [&_.slick-track]:h-full"
    >
      <Slider {...sliderSettings}>
        {slides.map((slide) => (
          <div key={slide.title} className="h-full">
            <div className="relative flex min-h-[100svh] w-full items-center justify-center overflow-hidden">
              {slide.image.endsWith(".mp4") ? (
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className={`absolute inset-0 h-full w-full object-cover ${slide.imageClass}`}
                >
                  <source src={slide.image} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={slide.image}
                  alt={slide.alt}
                  className={`absolute inset-0 h-full w-full object-cover ${slide.imageClass}`}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/50 to-slate-950/80" />

              <div className="relative z-20 mx-auto flex w-full max-w-5xl items-center justify-center px-6 pb-16 pt-28 text-center sm:px-10 md:px-14">
                <div className="max-w-3xl">
                  <h1
                    className="text-4xl inline-flex font-extrabold leading-tight text-white drop-shadow-[0_6px_18px_rgba(0,0,0,0.35)] sm:text-5xl md:text-6xl"
                    data-aos="fade-up"
                    data-aos-duration="800"
                  >
                    {slide.title}
                  </h1>
                  <p
                    className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-white/90 drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)] sm:text-base md:mt-6 md:text-lg md:leading-8"
                    data-aos="fade-up"
                    data-aos-delay="150"
                    data-aos-duration="800"
                  >
                    {slide.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </section>
  );
};

const HomeSlider = ({ openDemo }) => (
  <>
    <MobileHero openDemo={openDemo} />
    <DesktopSlider />
  </>
);

export default HomeSlider;
